/**
 * Shared route form validation hook
 *
 * Provides validation logic for route creation and editing forms.
 * Extracted to avoid code duplication between CreateRoutePage and EditRoutePage.
 */

import { useMemo } from 'react';
import type {
  Gym,
  RouteIdentification,
  RouteSetter,
  VGrade,
  GymLabelingMethods,
} from '@flash/shared';

/**
 * Form data structure for route validation
 */
export interface RouteFormValidationData {
  name: string;
  officialGrade: VGrade | null;
  identification: RouteIdentification;
}

/**
 * Configuration for route form validation
 */
export interface RouteFormValidationConfig {
  /** Whether route name is required (from gym settings) */
  requireRouteName: boolean;
  /** Whether gym uses color-only grading (no V-grades) */
  isColorOnly: boolean;
  /** Selected setters for the route */
  selectedSetters: RouteSetter[];
  /** Gym configuration for color grades and labeling methods (null when not loaded) */
  gym: Pick<Gym, 'colorGrades' | 'labelingMethods'> | null;
}

/**
 * Default labeling methods when gym doesn't have them configured
 */
const DEFAULT_LABELING_METHODS: GymLabelingMethods = {
  holdColors: true,
  tagColors: false,
  primaryMethod: 'holdColors',
  linkColors: true,
};

/**
 * Validate route info section of the form
 *
 * @param formData - Current form data
 * @param config - Validation configuration
 * @returns true if the section is valid, false otherwise
 */
export const validateRouteInfo = (
  formData: RouteFormValidationData,
  config: RouteFormValidationConfig
): boolean => {
  const { requireRouteName, isColorOnly, selectedSetters, gym } = config;

  // Name is required if gym setting is enabled
  if (requireRouteName && !formData.name.trim()) return false;

  // At least one setter required
  if (selectedSetters.length === 0) return false;

  // If not color-only, grade is required
  if (!isColorOnly && !formData.officialGrade) return false;

  // Check appearance/color if gym uses colors
  if (gym?.colorGrades?.length) {
    const labelingMethods = gym.labelingMethods ?? DEFAULT_LABELING_METHODS;

    // For color-only grading, a color selection IS the grade - require it
    // Otherwise, require based on which labeling methods are enabled
    const bothEnabled = labelingMethods.holdColors && labelingMethods.tagColors;
    const isLinked = bothEnabled && labelingMethods.linkColors;

    if (isLinked) {
      // Linked mode: holdColor is used for both (and is required)
      if (!formData.identification.holdColor) return false;
    } else {
      // Unlinked or single method mode - use primaryMethod to determine required
      const primaryMethod = labelingMethods.primaryMethod ?? 'holdColors';
      if (primaryMethod === 'holdColors' && !formData.identification.holdColor) {
        return false;
      }
      if (primaryMethod === 'tagColors' && !formData.identification.tagColor) {
        return false;
      }
      // If only one method enabled, require that one
      if (
        !labelingMethods.holdColors &&
        labelingMethods.tagColors &&
        !formData.identification.tagColor
      ) {
        return false;
      }
      if (
        labelingMethods.holdColors &&
        !labelingMethods.tagColors &&
        !formData.identification.holdColor
      ) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Hook for route form validation
 *
 * Provides memoized validation functions for route creation/editing forms.
 *
 * @example
 * ```tsx
 * const { validateRouteInfo, isRouteInfoValid } = useRouteFormValidation({
 *   formData,
 *   requireRouteName,
 *   isColorOnly,
 *   selectedSetters,
 *   gym,
 * });
 *
 * // Use in section status
 * const sectionStatus = {
 *   'route-info': isRouteInfoValid ? 'complete' : 'incomplete',
 * };
 * ```
 */
export const useRouteFormValidation = (
  formData: RouteFormValidationData,
  config: RouteFormValidationConfig
) => {
  const { requireRouteName, isColorOnly, selectedSetters, gym } = config;

  const isRouteInfoValid = useMemo(
    () => validateRouteInfo(formData, config),
    [
      requireRouteName,
      formData.name,
      formData.officialGrade,
      formData.identification.holdColor,
      formData.identification.tagColor,
      selectedSetters,
      isColorOnly,
      gym?.colorGrades?.length,
      gym?.labelingMethods,
      formData,
      config,
    ]
  );

  return {
    validateRouteInfo: () => validateRouteInfo(formData, config),
    isRouteInfoValid,
  };
};

export default useRouteFormValidation;
